/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import CloudDetailsCard from "./CloudCard";
import { ServiceDetailsPageSkeleton } from "kontrolEdge/components/Shared/ServiceDetailsPageSkeleton";
import { mapCloudDetailToSections } from "kontrolEdge/constants/constants";
import { fetchCloudConnectionAPI, fetchCloudErrorsAPI, fetchCloudHistoryAPI, fetchCloudProvidersAPI, fetchCloudSubscriptionAPI } from "../requests/request";
import { useAppSelector } from "@redux/hooks";
import NotificationsView from "kontrolEdge/components/Shared/Notifications/NotificationsView";
import { RootState } from "@redux/store/store";
import BandwidthChartUniversal from "kontrolEdge/components/Shared/Bandwidth/BandwidthChartUniversal";
import { DetailSummary } from "kontrolEdge/components/Shared/DetailSummary/DetailSummary";
import { toCapacityMbps } from "kontrolEdge/components/Shared/Bandwidth/bandwidthDisplay";
import { apiTagsToSummaries } from "kontrolEdge/components/Shared/NameTagEdit/useResourceTags";
import { useDetailTenantId } from "@shared/navigation/tabs/useDetailTenantId";
import { dropCachedFetch, readCachedFetch, writeCachedFetch } from "@hooks/useCachedFetch";
import { isTenantScopeForbidden, TENANT_OUT_OF_SCOPE_MESSAGE } from "@shared/helpers/tenantScope";
import ErrorFetchingItems from "@shared/components/ErrorFetching";

  export interface LatencyDetail {
    destination: string;
    timestamp: string;
    unit: string;
    value: number
  }
  export interface SubscriptionDetail {
    subscriptionId: string;
    contractMonths: string;
    autoRenew: string;
    renewDate: string;
    status: string;
    id: string;
  }

  export interface ProviderDetail {
    cloudRegion: string;
    cloudVLAN: string;
    sourceVLAN: string;
    state: string;
    connectionCloudId: string;
  }

  export interface CloudConnectivityDetail {
    id: string;                  
    renewDate: string;
    name: string;
    provider?: string;
    automaticVlan: string;
    lag?: {
      subid: string;
    }
    monitoring?:{
      enable: boolean
    }       
    customer?: { name: string };
    contractMonths: string;      
    subscriptionId: string;      
    cloud: string;     
    account: string;          
    cloudLocation: string;       
    connectionName: string;      
    mbps: string;                
    parentConnection: string;   
    parentConnectionId: string;
    tag: string;
    vlan: string;
    serviceStatus?: string;
    statusMessage?: string | null;
    performanceMetrics?: {
      errors: number;
      latency?: LatencyDetail
    };
    subscription?: SubscriptionDetail;
    providerInfo?: ProviderDetail;
    port: {
      id: number;
      name: string;
      portType?: "physical" | "virtual";
      datacenter: {
        datacenterId: string;
        name: string;
        displayName: string;
        direction: string;
        gps: string;
        capacityKW: string;
        phase: string;
        cage: string;
        rackId: string;
        rackCapacity: string;
        patchPanelODF: string;
        rackSecurityPin: string;
        status: string;
        visible: boolean;
        providerCode: string;
        city: string;
        providerId: number;
        createdAt: string;
        updatedAt: string;
      };
    };            
  }

/**
 * First-paint shortcut from the list, never a requirement: the detail fetch below is what
 * the page renders, so a pasted URL paints the same screen (CTX-773). The tenant the
 * connection is read in travels in `?tenantId=` — see `useDetailTenantId`.
 */
type LocationState = { connectionDetail?: CloudConnectivityDetail };

const CloudConnectivityDetails: React.FC = () => {
    const location = useLocation();
    const reduxTenantId = useAppSelector((state) => state.selection.tenantId);
    const navigate = useNavigate();

    const { connectionDetail: preloaded } = (location.state as LocationState | null) ?? {};
    const [searchParams] = useSearchParams();
    // Decide which one to actually send to every API call:
    const effectiveTenantId = useDetailTenantId();
    // The last assembled answer for this resource paints at once when the user comes back
    // (a view switch, Back); the requests below still run and replace it.
    const detailCacheKey = searchParams.get("connectionId") ? `kontroledge/cloud-connectivity/${searchParams.get("connectionId")}?tenantId=${effectiveTenantId}` : null;
    const [detail, setDetail] = useState<CloudConnectivityDetail | undefined>(
      () => readCachedFetch<CloudConnectivityDetail>(detailCacheKey) ?? preloaded
    );
    // Starts as fetching: with no row to seed from, the first frame would otherwise read
    // "not found" before the fetch effect has had a chance to run.
    const [isFetching, setIsFetching] = useState(() => Boolean(searchParams.get("connectionId")) && !readCachedFetch(detailCacheKey));
    // Only the tenant refusal (CTX-866): any other failure still reads as "not found".
    const [error, setError] = useState<string | null>(null);
    const api = useAxiosAuth0();
    const userProfile = useAppSelector((state: RootState) => state.userProfile);
    const { canAccessAlerts } = userProfile;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const connectionId = searchParams.get("connectionId")!;
        
    // Track the initial tenantId when component mounts to detect changes
      const initialTenantIdRef = useRef<number | null>(null);
  
    // Initialize the ref on mount
    useEffect(() => {
      if (initialTenantIdRef.current === null) {
        initialTenantIdRef.current = reduxTenantId;
      }
    }, []);

    // Redirect to main view if tenantId changes while on detail page
    useEffect(() => {
      // Skip if ref hasn't been initialized yet
      if (initialTenantIdRef.current === null) return;
      // Only redirect if tenantId actually changed (not on initial mount)
      if (reduxTenantId !== initialTenantIdRef.current) {
        navigate("/kontrol/cloud-connectivity");
      }
    }, [reduxTenantId, navigate]);

    const sections = useMemo(
      () => mapCloudDetailToSections(detail),
        [detail]
    );

    // Rich tags inline from the detail GET (§5.5). Forwarded to the card so
    // `useResourceTags` skips its first §5.1 round-trip when the modal opens.
    const initialAssignedTags = useMemo(
      () => apiTagsToSummaries((detail as { tags?: unknown } | undefined)?.tags),
      [detail]
    );

    // Tenant the resource actually lives in. See PortDetails.tsx for the
    // rationale (partner-view writes need this — the lens-derived
    // `useEffectiveTenantId` 404s when the connection lives in a child).
    //
    // Falls back to `effectiveTenantId` (the same value the detail GET
    // sent as `?tenantId=`) when `raw.tenantId` is not exposed on the
    // response body — keeps the picker / attach calls scoped to the
    // resource's tenant instead of leaking to the JWT tenant.
    const resourceTenantId = useMemo(() => {
      const t = (detail as { tenantId?: unknown } | undefined)?.tenantId;
      if (typeof t === "number" && Number.isFinite(t)) return t;
      return effectiveTenantId ?? undefined;
    }, [detail, effectiveTenantId]);

    // From the detail itself — the chart only mounts once it has loaded.
    // Unknown -> the chart treats it as virtual (30m).
    const portType = detail?.port?.portType;
    //Temporary limit for the operational cloud historical data
    const limit = 500;

        useEffect(() => {
          //Re-run whenever connectionId or refresh changes
          if (!connectionId) return;
          let cancelled = false;
          setError(null);
          // Only a first visit waits on a skeleton; a cached answer stays up while this refreshes.
          if (!readCachedFetch(detailCacheKey)) setIsFetching(true);
        
          (async () => {
            try {
              //Core detail – show it immediately
              const coreResp = await fetchCloudConnectionAPI(api, connectionId, effectiveTenantId);
              const fresh: CloudConnectivityDetail = coreResp.data.data;

              // Calculate parentConnectionId if not provided (handles both PORT and LAG cases)
              if (!fresh.parentConnectionId || fresh.parentConnectionId === "-") {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const lagId = (fresh as any)?.lag?.id;
                const portId = fresh.port?.id;
                fresh.parentConnectionId = (portId ? String(portId) : lagId ? String(lagId) : "-");
              }

              if (cancelled) return;
              setDetail(fresh);
        
              //Extras in parallel
              const [
                errorsSettled,
                subscriptionSettled,
                providersSettled,
                historySettled
              ] = await Promise.allSettled([
                fetchCloudErrorsAPI(api, connectionId, effectiveTenantId),
                fetchCloudSubscriptionAPI(api, connectionId,effectiveTenantId),
                fetchCloudProvidersAPI(api, connectionId,effectiveTenantId),
                fetchCloudHistoryAPI(api, connectionId, effectiveTenantId, limit),
              ]);
        
              //Merge successful results
              if (errorsSettled.status === 'fulfilled') {
                fresh.performanceMetrics = {
                  ...fresh.performanceMetrics,
                  errors: errorsSettled.value.data.data.crcErrors
                };
              } else {
                console.warn('CRC errors failed:', errorsSettled.reason);
              }
              if (subscriptionSettled.status === 'fulfilled') {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (fresh as any).subscription = subscriptionSettled.value.data.data;
              } else {
                console.warn('Subscription fetch failed:', subscriptionSettled.reason);
              }
              if (providersSettled.status === 'fulfilled') {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (fresh as any).providerInfo = providersSettled.value.data.data;
              } else {
                console.warn('Providers fetch failed:', providersSettled.reason);
              }
              if (historySettled.status === "fulfilled") {
                (fresh as any).operationalHistory = historySettled.value.data.data;
              } else {
                console.warn("History fetch failed:", historySettled.reason);
              }
              
        
              if (cancelled) return;
              //Push the merged object back into state
              writeCachedFetch(detailCacheKey, { ...fresh });
              setDetail({ ...fresh });
        
            } catch (err) {
              console.error('Error loading InternetDetails:', err);
              // A tenant outside the caller's scope: forget what was cached for it and show
              // the refusal, not the connection's last known state.
              if (isTenantScopeForbidden(err)) {
                dropCachedFetch(detailCacheKey);
                if (!cancelled) {
                  setDetail(undefined);
                  setError(TENANT_OUT_OF_SCOPE_MESSAGE);
                }
              }
            } finally {
              if (!cancelled) setIsFetching(false);
            }
          })();
        
          return () => { cancelled = true; };
        }, [api, connectionId]);    

    if (isFetching) {
      return <ServiceDetailsPageSkeleton />;
    }
    if (error) return <ErrorFetchingItems text={error} />;
    if (!detail) {
      return (
        <p className="p-4 text-sm text-semantic-text-secondary">
          Cloud connection not found or could not be loaded.
        </p>
      );
    }

    return (
        <>
            {/* On a phone the chart follows the summary, above the folded cards (CTX-1421). */}
            <div className="flex flex-col">
            <DetailSummary sections={sections} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4 auto-rows-auto">
                {sections.map((section, idx) => (
                    <CloudDetailsCard key={idx} data={section} connectionDetail={detail} tenantId={effectiveTenantId} initialAssignedTags={initialAssignedTags} resourceTenantId={resourceTenantId}/>
                ))}
            </div>
            {/* Bandwidth Utilization chart */}
            <div className="-order-1 sm:order-none">
            <BandwidthChartUniversal
                service="cloud"
                id={connectionId}
                tenantId={effectiveTenantId}
                portType={portType}
                capacityMbps={toCapacityMbps(detail?.mbps)}
            />
            </div>
            </div>
            {/* {canAccessAlerts && connectionId && (
              <NotificationsView serviceType="cloud" serviceId={connectionId}/>
            )} */}
        </>
    );
};

export default CloudConnectivityDetails;
